/**
 * sankeyEngine.js — Calculates Sankey flow data from projection state.
 *
 * Pure functions — zero React.
 * Takes a single year's projection data and returns structured
 * nodes and links for the SVG renderer.
 *
 * Flow structure:
 *   Income (comp) → Tax | After-Tax
 *   After-Tax → Savings | Living | Mortgage | Surplus
 */

import { FF } from '../../utils/format';

/**
 * Build Sankey flow data for a given projection year.
 *
 * @param {object} d - single year from projection array
 * @param {object} s - app state (for hAge check)
 * @returns {object} { nodes, links, totalIncome }
 */
export function buildSankeyData(d, s) {
  if (!d || !d.comp) {
    return { nodes: [], links: [], totalIncome: 0 };
  }

  const comp = d.comp;
  const totalTax = d.taxFed + d.taxProv + d.taxCPP + d.taxEI;
  const afterTax = d.afterTax;
  const savings = Math.max(0, d.sv);
  const living = d.liv;
  const mortgage = d.a >= s.hAge && d.moP > 0 ? d.moP * 12 : 0;
  const surplus = Math.max(0, d.sur);
  const deficit = Math.max(0, -d.sur);

  /* ─── NODES ─── */
  // Left column (source)
  const nodes = [
    { id: "income", label: "Income", value: comp, column: 0 },
  ];

  // Middle column (first split)
  const midNodes = [
    { id: "tax", label: "Tax", value: totalTax, column: 1 },
    { id: "after-tax", label: "After Tax", value: afterTax, column: 1 },
  ];

  // Right column (second split)
  const rightNodes = [];
  if (savings > 0) rightNodes.push({ id: "savings", label: "Savings", value: savings, column: 2 });
  if (living > 0) rightNodes.push({ id: "living", label: "Expenses", value: living, column: 2 });
  if (mortgage > 0) rightNodes.push({ id: "mortgage", label: "Mortgage", value: mortgage, column: 2 });
  if (surplus > 0) rightNodes.push({ id: "surplus", label: "Surplus", value: surplus, column: 2 });
  if (deficit > 0) rightNodes.push({ id: "deficit", label: "Deficit", value: deficit, column: 2 });

  nodes.push(...midNodes, ...rightNodes);

  /* ─── LINKS ─── */
  const links = [
    { source: "income", target: "tax", value: totalTax },
    { source: "income", target: "after-tax", value: afterTax },
  ];

  if (savings > 0) links.push({ source: "after-tax", target: "savings", value: savings });
  if (living > 0) links.push({ source: "after-tax", target: "living", value: living });
  if (mortgage > 0) links.push({ source: "after-tax", target: "mortgage", value: mortgage });
  if (surplus > 0) links.push({ source: "after-tax", target: "surplus", value: surplus });
  if (deficit > 0) links.push({ source: "after-tax", target: "deficit", value: deficit });

  return { nodes, links, totalIncome: comp };
}

/**
 * Color mapping for Sankey nodes.
 */
export function getNodeColor(nodeId, c) {
  const map = {
    "income":    c.blue,
    "tax":       c.red,
    "after-tax": c.fg,
    "savings":   c.green,
    "living":    c.orange,
    "mortgage":  c.purple || "#BF5AF2",
    "surplus":   c.teal || "#6AC4DC",
    "deficit":   c.red,
  };
  return map[nodeId] || c.muted;
}

/**
 * Monthly display value.
 */
export function monthlyLabel(annual) {
  return `${FF(Math.round(annual / 12))}/mo`;
}
